import React, { useCallback, useMemo, useRef } from "react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { TrashIcon, type TrashIconHandle } from "@/components/ui/trash"

const NOTIFICATION_DELETE_BUTTON_CLASS =
  "size-9 shrink-0 border-destructive/40 text-destructive shadow-sm hover:bg-destructive/10 hover:text-destructive focus-visible:border-destructive/60 focus-visible:ring-destructive/25 dark:border-destructive/50 dark:hover:bg-destructive/15 [&_svg]:text-current"

export function NotificationDetailDeleteButton({
  onClick,
  disabled = false,
  className,
}: {
  onClick: () => void
  disabled?: boolean
  className?: string
}): React.ReactElement {
  const deleteIconRef = useRef<TrashIconHandle | null>(null)

  const startDeleteIconAnimation = useCallback(() => {
    void deleteIconRef.current?.startAnimation()
  }, [])

  const stopDeleteIconAnimation = useCallback(() => {
    void deleteIconRef.current?.stopAnimation()
  }, [])

  const deleteIconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startDeleteIconAnimation,
      onMouseEnter: startDeleteIconAnimation,
      onMouseLeave: stopDeleteIconAnimation,
    }),
    [startDeleteIconAnimation, stopDeleteIconAnimation]
  )

  const handleClick = useCallback(() => {
    onClick()
    window.setTimeout(() => {
      void deleteIconRef.current?.stopAnimation()
    }, 260)
  }, [onClick])

  return (
    <Button
      type="button"
      variant="outline"
      size="icon-sm"
      data-font="primary"
      data-trade-log-action="delete"
      data-notification-detail-delete=""
      className={cn(NOTIFICATION_DELETE_BUTTON_CLASS, className)}
      disabled={disabled}
      aria-label="Delete notification"
      onClick={handleClick}
      {...deleteIconHoverHandlers}
    >
      <TrashIcon
        ref={deleteIconRef}
        size={16}
        className="shrink-0 text-current"
        aria-hidden
      />
    </Button>
  )
}
